import clsx from 'clsx'
import { Link } from 'react-router'
import { cutShape, splitFacetValue } from '@/site/lib/format'
import { isReservedParam, SEARCH_FACETS, type SearchFilters as SearchState } from '@/site/lib/search'
import type { Facet, FacetOption } from '@/site/lib/types'

interface Props {
  searchFilters: SearchState
  facets: Facet[]
  /** Query string (?…) that toggles a facet value / removes a filter. */
  selectHref: (field: string, value: string) => string
  removeHref: (field: string, value: string) => string
}

const VISIBLE_OPTIONS = 8
const CUT_BG = ['bg-qt-block-1', 'bg-qt-block-2', 'bg-qt-block-3', 'bg-qt-block-4', 'bg-qt-block-5', 'bg-qt-block-6']

function selectedValues(filters: SearchState, field: string): string[] {
  const v = filters[field]
  return v === undefined ? [] : Array.isArray(v) ? v : [v]
}

/** Active filters (and the search term) as removable pills. */
export function ActiveFilters({ searchFilters, removeHref }: Pick<Props, 'searchFilters' | 'removeHref'>) {
  const query = typeof searchFilters.q === 'string' ? searchFilters.q : undefined
  const pills = [
    ...(query ? [{ field: 'q', value: query, label: `“${query}”` }] : []),
    ...Object.keys(searchFilters)
      .filter((key) => !isReservedParam(key))
      .flatMap((field) => selectedValues(searchFilters, field).map((value) => ({ field, value, label: splitFacetValue(field, value).name }))),
  ]
  if (pills.length === 0) return null

  return (
    <ul className="flex flex-wrap gap-2" aria-label="Active filters">
      {pills.map((p) => (
        <li key={`${p.field}-${p.value}`}>
          <Link
            to={{ search: removeHref(p.field, p.value) }}
            preventScrollReset
            aria-label={`Remove filter: ${p.label}`}
            className="flex items-center gap-2 rounded-xl bg-qt-primary py-1.5 pr-2.5 pl-3 text-sm font-bold text-white hover:bg-qt-primary-hover"
          >
            {p.label}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function OptionLink({ field, opt, selected, href, index }: { field: string; opt: FacetOption; selected: boolean; href: string; index: number }) {
  const { name, size } = splitFacetValue(field, opt.value)
  const label = `${[name, size].filter(Boolean).join(' ')}, ${opt.count} ${opt.count === 1 ? 'tutorial' : 'tutorials'}`

  if (field === 'Fabric Cut') {
    return (
      <Link to={{ search: href }} preventScrollReset aria-label={label} aria-current={selected || undefined} className={clsx('group flex min-h-11 items-center gap-3 rounded-xl p-1.5', selected ? 'bg-white ring-2 ring-qt-primary' : 'hover:bg-white')}>
        <span className={clsx('flex size-10 shrink-0 items-center justify-center rounded-lg', CUT_BG[index % CUT_BG.length])} aria-hidden="true">
          <svg viewBox="0 0 72 72" className="size-6">
            <path d={cutShape(opt.value)} fill="#fff" fillOpacity="0.92" />
          </svg>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold group-hover:underline">{name}</span>
          {size && <span className="block text-xs font-semibold text-qt-muted">{size}</span>}
        </span>
        <span className="text-xs font-bold text-qt-muted">{opt.count}</span>
      </Link>
    )
  }

  return (
    <Link to={{ search: href }} preventScrollReset aria-label={label} aria-current={selected || undefined} className="group flex min-h-11 items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-sm font-semibold hover:bg-white lg:min-h-0">
      <span className={clsx('flex size-5 shrink-0 items-center justify-center rounded-md border-2', selected ? 'border-qt-primary bg-qt-primary text-white' : 'border-qt-muted/50 bg-white')} aria-hidden="true">
        {selected && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round">
            <path d="m5 12 5 5 9-10" />
          </svg>
        )}
      </span>
      <span className="flex-1 group-hover:underline">{name}</span>
      <span className="text-xs font-bold text-qt-muted">{opt.count}</span>
    </Link>
  )
}

/** Search sidebar: the facet lists, as links (they work before hydration and search engines can follow them). */
export default function SearchFilters({ searchFilters, facets, selectHref }: Props) {
  return (
    <div className="flex flex-col divide-y divide-qt-stitch/30">
      {SEARCH_FACETS.map((field) => {
        const facet = facets.find((f) => f.field === field)
        if (!facet?.options?.length) return null
        const selected = selectedValues(searchFilters, field)
        const render = (opt: FacetOption, i: number) => (
          <li key={opt.value}>
            <OptionLink field={field} opt={opt} index={i} selected={selected.includes(opt.value)} href={selectHref(field, opt.value)} />
          </li>
        )
        const first = facet.options.slice(0, VISIBLE_OPTIONS)
        const rest = facet.options.slice(VISIBLE_OPTIONS)

        return (
          <section key={field} aria-label={facet.displayName} className="py-4 first:pt-0 last:pb-0">
            <h3 className="mb-2 text-sm font-extrabold tracking-wide text-qt-ink uppercase">{facet.displayName}</h3>
            <ul className="flex flex-col gap-0.5">{first.map(render)}</ul>
            {rest.length > 0 && (
              <details className="group">
                <summary className="mt-1 list-none px-1.5 py-1 text-sm font-bold text-qt-primary hover:underline [&::-webkit-details-marker]:hidden">
                  <span className="group-open:hidden">Show {rest.length} more</span>
                  <span className="hidden group-open:inline">Show fewer</span>
                </summary>
                <ul className="flex flex-col gap-0.5">{rest.map((opt, i) => render(opt, i + VISIBLE_OPTIONS))}</ul>
              </details>
            )}
          </section>
        )
      })}
    </div>
  )
}
